import { retrieveGroupMapper } from "./mapper/mapper";
import { IClient } from "../../common/interfaces/IClient";
import { IGroupResult } from "../interfaces/IGroupResult";
import { QuoteSettingContainerType } from "../../common/interfaces/settings/QuoteSettingContainerType";
import { IContactDetails } from "../../common/interfaces/IContactDetails";
import { ApiResponseMessage, ApiResponseNoWrap } from "../types/api/ApiResponses";
import { SortBy } from "../enums/SortBy";
import ProductType from "../../common/interfaces/enums/ProductType";
import { HttpMethod } from "../enums/HttpMethod";
import { SmokerStatus } from "../../common/interfaces/enums/SmokerStatus";
import { IApiGroupData } from "../interfaces/api/group/IApiGroupData";
import { Gender } from "../../common/interfaces/enums/Gender";
import IApiGroupClient from "../interfaces/api/group/case/config/clients/IApiGroupClient";
import IApiAboutInsurer from "../interfaces/api/IApiAboutInsurer";
import IBenefitRatings from "../interfaces/IBenefitRatings";
import { apiFetch, parseGroupResult } from "./helpers";
import useErrorLogger from "../../common/hooks/useErrorLogger";
import { addQuoteToGroupAPI, createQuoteAPI } from "./endpoints/ApiQuotes";
import IGroupCase from "../interfaces/IGroupCase";

const ErrorLogger = useErrorLogger();

/*
    Quote Group Endpoints
*/

export const getGroupAPI = async (groupId: string): Promise<IGroupResult> => {
    return apiFetch<ApiResponseMessage<IApiGroupData>>(`/api/v1/groups/${groupId}`, {
        method: HttpMethod.Get
    }).then((res) => parseGroupResult(res.data));
};

export const createGroupAPI = async (): Promise<IGroupResult> => {
    return apiFetch<ApiResponseMessage<IApiGroupData>>(`/api/v1/groups`, {
        method: HttpMethod.Post
    }).then((res) => parseGroupResult(res.data));
};

export const createOrSetClientsAPI = async (
    groupId: string,
    clients: IClient[]
): Promise<IGroupResult> => {
    const content = clients.map(
        (c): IApiGroupClient => ({
            isActive: c.isActive,
            client: {
                personId: c.personId,
                age: c.age,
                gender: c.gender || Gender.Unknown,
                isSmoker: c.smokerStatus === SmokerStatus.Smoker,
                isChild: c.isChild
            }
        })
    );

    return apiFetch<ApiResponseMessage<IApiGroupData>>(
        `/api/v1/groups/${groupId}/persons`,
        {
            method: HttpMethod.Post,
            headers: {
                "content-type": "application/json"
            },
            body: JSON.stringify(content)
        }
    ).then((res) => parseGroupResult(res.data));
};

export const favouriteOrSelectProductAPI = (
    groupId: string,
    caseId: number,
    productId: number,
    isSelected: boolean,
    isFaved: boolean
): Promise<IGroupResult> => {
    return apiFetch<ApiResponseMessage<IApiGroupData>>(
        `/api/v1/groups/${groupId}/cases/${caseId}/products/${productId}?isFavourited=${isFaved}&isSelected=${isSelected}`,
        {
            method: HttpMethod.Post,
            headers: {
                "content-type": "application/json"
            }
        }
    ).then((res) => parseGroupResult(res.data));
};

/*
    Quotes Config Endpoints
*/

export const changeSortOrderAPI = async (
    caseId: number,
    sortId: SortBy,
    productType: ProductType
): Promise<boolean> => {
    return fetch(`/api/v1/${productType}/quote/config/${caseId}`, {
        method: HttpMethod.Patch,
        headers: {
            "content-type": "application/json"
        },
        body: JSON.stringify({ sortBy: sortId.toString().toLowerCase() })
    }).then((res) => !!res); // TODO: We should probably return a success message.
};

/*
    Contact Details Endpoints
*/
export const setContactDetailsAPI = async (
    groupId: string,
    details: IContactDetails,
    caseId: number
): Promise<boolean> => {
    return apiFetch<ApiResponseMessage<null>>(
        `/api/v1/groups/${groupId}/contact?name=${encodeURIComponent(
            details.name
        )}&email=${encodeURIComponent(details.emailAddress)}&phone=${encodeURIComponent(
            details.phoneNumber
        )}${caseId > 0 ? `&CaseID=${caseId}` : ""}`,
        {
            method: HttpMethod.Post,
            headers: {
                "content-type": "application/json"
            }
        }
    ).then((res) => res.isSuccessful);
};

/* 
    Utility Endpoints
*/

export const getAboutInsurer = async (providerId: number): Promise<IApiAboutInsurer> => {
    return apiFetch<ApiResponseNoWrap<IApiAboutInsurer>>(
        `/api/insurers/${providerId}/about`,
        {
            method: HttpMethod.Get,
            headers: {
                "content-type": "application/json"
            }
        }
    );
};

type OccupationSuggestion = {
    occupation: string;
    class: string; //"1" | "2" | "3" | "4" | "U" | "u" | "IC" | ;
};

export const getBenefitRatings = async (
    benefitTypeId: number
): Promise<IBenefitRatings[]> => {
    return apiFetch<ApiResponseNoWrap<IBenefitRatings[]>>(
        `/api/insurers/benefits/ratings/${benefitTypeId}/`,
        {
            method: HttpMethod.Get
        }
    );
};

export const getOccupationSuggestions = async (
    occupationSegment: string
): Promise<OccupationSuggestion[]> => {
    return apiFetch<ApiResponseNoWrap<OccupationSuggestion[]>>(
        `/api/occupationsearch?searchCriteria=${encodeURIComponent(occupationSegment)}`,
        {
            method: HttpMethod.Get,
            headers: {
                "content-type": "application/json"
            }
        }
    );
};


export const addExistingQuotes = async (
    clients: IClient[],
    groupCases: IGroupCase[]
): Promise<string> => {
    try {
        // Attach new Group Id to Visitor Id
        const groupId = await createGroupAPI().then((res) => res.groupId);
        // Attach People to Group ???

        await createOrSetClientsAPI(groupId, clients);
        // Attach Setting/Case to group

        const casePromises: Promise<void>[] = groupCases.map(async (groupCase) => {
            const mapper = retrieveGroupMapper(groupCase.config.productType);
            const caseId = await createQuoteAPI(mapper, clients, groupCase.config);
            await addQuoteToGroupAPI(groupId, caseId, groupCase.isSelected);
        });

        await Promise.all(casePromises);

        return groupId;
    } catch (e) {
        ErrorLogger.Log(e, {
            source: "API - Add existing quote"
        });
        return "";
    }
};
